import { Tag, Package, Link2, CheckCircle } from 'lucide-react'

interface PreviewPlaceholderProps {
  step: number
  paymentType?: 'cause-service' | 'products-subscriptions'
}

const STEP_PLACEHOLDERS = {
  1: {
    icon: Tag,
    title: 'General',
    description: 'Select your payment type, add content, and configure pricing',
    color: 'text-blue-500',
    bgColor: 'bg-blue-50'
  },
  2: {
    icon: Link2,
    title: 'Configure your link',
    description: 'Set your custom URL and advanced options',
    color: 'text-orange-500',
    bgColor: 'bg-orange-50'
  },
  3: {
    icon: CheckCircle,
    title: 'Almost done!',
    description: 'Choose what happens after a successful payment',
    color: 'text-teal-500',
    bgColor: 'bg-teal-50'
  }
}

export function PreviewPlaceholder({ step, paymentType }: PreviewPlaceholderProps) {
  const placeholder = STEP_PLACEHOLDERS[step as keyof typeof STEP_PLACEHOLDERS] || STEP_PLACEHOLDERS[1]
  const Icon = placeholder.icon

  return (
    <div className="flex items-center justify-center min-h-[500px] p-8">
      <div className="max-w-md text-center">
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${placeholder.bgColor} mb-6`}>
          <Icon className={`h-10 w-10 ${placeholder.color}`} strokeWidth={1.5} />
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-foreground mb-3">
          {placeholder.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-6">
          {placeholder.description}
        </p>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                s === step
                  ? `w-8 ${placeholder.color.replace('text-', 'bg-')}`
                  : s < step
                  ? 'w-1.5 bg-green-400'
                  : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Step Counter */}
        <div className="mt-4 text-xs font-medium text-muted-foreground">
          Step {step} of 3
        </div>

        {/* Payment Type Badge */}
        {paymentType && (
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-neutral-100 border border-border">
            {paymentType === 'products-subscriptions' ? (
              <>
                <Package className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-medium text-foreground">Products</span>
              </>
            ) : (
              <>
                <Tag className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs font-medium text-foreground">Cause/Service</span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
